import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  ButtonGroup,
  Flex,
  Icon,
  IconButton,
  Menu,
  Separator,
  Heading,
} from "@chakra-ui/react-v3";
import { InfoIcon } from "@chakra-ui/icons";
import { FaChevronDown } from "react-icons/fa";
import { SuspectRowData } from "../tables/suspect";
import { suspectMapping } from "../../shared-components/fhir-sections/suspects/shared";
import type { SuspectingEvidence, SuspectFhir } from "../../../../api/suspects";
import { useDownloadFile } from "../../shared-logic/useDownloadFile";
import { SidePanelLayout } from "../components/side-panel-sections/side-panel-layout";
import { ResourceAnalysis } from "../components/resource-analysis";
import { useDocumentCallbackContext } from "./sidepanel-context";

export type SuspectSidePanelContentProps = {
  data: SuspectRowData;
};

/**
 * Side panel content for suspect conditions.
 * Shows condition details, detection logic, evidence, and optionally AI analysis.
 * Close button is handled generically by SidePanelDrawer.
 */
export function SuspectSidePanelContent({ data }: SuspectSidePanelContentProps) {
  const { onPdfView } = useDocumentCallbackContext();
  const mapping = suspectMapping[data.group as keyof typeof suspectMapping];

  return (
    <>
      <SidePanelLayout title="Suspect Condition" subtitle={data.condition}>
        {/* AI Analysis Section (if available) */}
        <ResourceAnalysis />

        {/* Logic Tooltip Section */}
        {mapping?.logicTooltip && (
          <Box>
            <HStack gap={2} align="start">
              <Icon as={InfoIcon} color="status.info" mt={0.5} />
              <VStack align="start" gap={1}>
                <Text fontSize="sm" fontWeight="medium" color="fg">
                  Detection Logic:
                </Text>
                <Text fontSize="sm" color="fg.muted">
                  {mapping.logicTooltip}
                </Text>
              </VStack>
            </HStack>
          </Box>
        )}

        <Separator borderColor="border" />

        {/* Evidence Section */}
        <SuspectingEvidenceSection
          suspectingEvidence={data.suspectingEvidence}
          onViewPdf={onPdfView}
        />
      </SidePanelLayout>
    </>
  );
}

function SuspectingEvidenceSection({
  suspectingEvidence,
  onViewPdf,
}: {
  suspectingEvidence: SuspectingEvidence[];
  onViewPdf?: (docId: string) => void;
}) {
  if (!suspectingEvidence || suspectingEvidence.length === 0) {
    return (
      <Box>
        <Heading size="md" mb={3} color="fg">
          Evidence
        </Heading>
        <Text fontSize="sm" color="fg.muted">
          No evidence found
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="md" mb={3} color="fg">
        Evidence ({suspectingEvidence.length})
      </Heading>
      <VStack gap={2} align="stretch">
        {suspectingEvidence
          .sort((a, b) => {
            const aFhir = a.fhir as Record<string, unknown> | undefined;
            const bFhir = b.fhir as Record<string, unknown> | undefined;

            const aDateStr = (aFhir?.authoredOn ?? aFhir?.effectiveDateTime) as string | undefined;
            const bDateStr = (bFhir?.authoredOn ?? bFhir?.effectiveDateTime) as string | undefined;

            const aDate = aDateStr ? new Date(aDateStr).getTime() : 0;
            const bDate = bDateStr ? new Date(bDateStr).getTime() : 0;

            return bDate - aDate;
          })
          .map((evidence, index) => (
            <SuspectingEvidenceItem
              key={`${evidence.id}-${index}`}
              evidence={evidence}
              onViewPdf={onViewPdf}
            />
          ))}
      </VStack>
    </Box>
  );
}

function SuspectingEvidenceItem({
  evidence,
  onViewPdf,
}: {
  evidence: SuspectingEvidence;
  onViewPdf?: (docId: string) => void;
}) {
  const { isDownloading, onDownloadFile } = useDownloadFile();

  evidence.resource_type =
    evidence.resource_type.charAt(0).toUpperCase() + evidence.resource_type.slice(1);

  const hasFhirData = Boolean(evidence.fhir);

  if (!hasFhirData) {
    return (
      <Box border="1px" borderColor="border" borderRadius="lg" p={4} bg="bg">
        <Text fontSize="sm" color="fg.muted">
          No FHIR data available
        </Text>
      </Box>
    );
  }

  return (
    <SuspectFhirResourceCard
      suspectData={evidence.fhir as SuspectFhir}
      dataSource={evidence.data_source}
      isDownloading={isDownloading}
      onDownloadFile={onDownloadFile}
      onViewPdf={onViewPdf}
    />
  );
}

function SuspectFhirResourceCard({
  suspectData,
  dataSource,
  isDownloading,
  onDownloadFile,
  onViewPdf,
}: {
  suspectData: SuspectFhir;
  dataSource?: string;
  isDownloading: Record<string, { downloading: boolean; type?: string }>;
  onDownloadFile: (params: {
    docId: string;
    docFilename: string;
    docExtension?: string;
    conversionType?: "html" | "pdf";
  }) => void;
  onViewPdf?: (docId: string) => void;
}) {
  function getResourceTypeColor(type: string): string {
    const lowerType = type.toLowerCase();
    if (lowerType.includes("observation")) return "blue";
    if (lowerType.includes("procedure")) return "purple";
    if (lowerType.includes("condition")) return "orange";
    if (lowerType.includes("medication")) return "green";
    if (lowerType.includes("diagnostic")) return "cyan";
    if (lowerType.includes("immunization")) return "teal";
    if (lowerType.includes("allergyintolerance") || lowerType.includes("allergy")) return "red";
    if (lowerType.includes("encounter")) return "pink";
    return "gray";
  }

  const resourceType = suspectData.resourceType;

  function getName(): string {
    const data = suspectData as Record<string, unknown>;

    /** Check for medicationCodeableConcept first (MedicationRequest) */
    if (data.medicationCodeableConcept) {
      const medCode = data.medicationCodeableConcept as Record<string, unknown>;
      if (medCode.text && typeof medCode.text === "string") {
        return medCode.text;
      }
      if (medCode.coding && Array.isArray(medCode.coding) && medCode.coding[0]) {
        const coding = medCode.coding[0] as Record<string, unknown>;
        if (coding.display && typeof coding.display === "string") {
          return coding.display;
        }
      }
    }

    /** Fallback to code field (Observation, Procedure, etc.) */
    const code = suspectData.code as Record<string, unknown> | undefined;
    if (code?.coding && Array.isArray(code.coding) && code.coding[0]) {
      const coding = code.coding[0] as Record<string, unknown>;
      if (coding.display && typeof coding.display === "string") {
        return coding.display;
      }
    }
    if (code?.text && typeof code.text === "string") {
      return code.text;
    }
    return suspectData.resourceType as string;
  }

  function getCode(): string | undefined {
    const data = suspectData as Record<string, unknown>;

    /** Check for medicationCodeableConcept first (MedicationRequest) */
    if (data.medicationCodeableConcept) {
      const medCode = data.medicationCodeableConcept as Record<string, unknown>;
      if (medCode.coding && Array.isArray(medCode.coding) && medCode.coding[0]) {
        const coding = medCode.coding[0] as Record<string, unknown>;
        if (coding.code && typeof coding.code === "string") {
          return coding.code;
        }
      }
    }

    /** Fallback to code field (Observation, Procedure, etc.) */
    const code = suspectData.code as Record<string, unknown> | undefined;
    if (code?.coding && Array.isArray(code.coding) && code.coding[0]) {
      const coding = code.coding[0] as Record<string, unknown>;
      if (coding.code && typeof coding.code === "string") {
        return coding.code;
      }
    }
    return undefined;
  }

  function getValue(): string | undefined {
    const data = suspectData as Record<string, unknown>;
    if (suspectData.valueQuantity) {
      const vq = suspectData.valueQuantity as Record<string, unknown>;
      const value = vq.value;
      const unit = vq.unit && typeof vq.unit === "string" ? vq.unit : "";
      return `${value} ${unit}`.trim();
    }
    if (data.valueString && typeof data.valueString === "string") {
      return data.valueString;
    }
    if (data.valueCodeableConcept) {
      const vcc = data.valueCodeableConcept as Record<string, unknown>;
      if (vcc.text && typeof vcc.text === "string") {
        return vcc.text;
      }
      if (vcc.coding && Array.isArray(vcc.coding) && vcc.coding[0]) {
        const coding = vcc.coding[0] as Record<string, unknown>;
        if (coding.display && typeof coding.display === "string") {
          return coding.display;
        }
      }
    }
    if (typeof data.valueBoolean === "boolean") {
      return data.valueBoolean ? "Yes" : "No";
    }
    if (typeof data.valueInteger === "number") {
      return data.valueInteger.toString();
    }
    return undefined;
  }

  function getEffectiveDate(): string | undefined {
    const data = suspectData as Record<string, unknown>;

    // Check authoredOn (MedicationRequest)
    if (data.authoredOn && typeof data.authoredOn === "string") {
      try {
        return new Date(data.authoredOn).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch {
        return undefined;
      }
    }

    // Check effectiveDateTime (Observation, Procedure, etc.)
    if (suspectData.effectiveDateTime && typeof suspectData.effectiveDateTime === "string") {
      try {
        return new Date(suspectData.effectiveDateTime).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch {
        return undefined;
      }
    }
    if (data.effectivePeriod) {
      const period = data.effectivePeriod as Record<string, unknown>;
      if (period.start && typeof period.start === "string") {
        try {
          return new Date(period.start).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        } catch {
          return undefined;
        }
      }
    }
    return undefined;
  }

  function getInterpretation(): string | undefined {
    const data = suspectData as Record<string, unknown>;
    if (data.interpretation && Array.isArray(data.interpretation)) {
      const interp = data.interpretation[0] as Record<string, unknown> | undefined;
      if (interp?.text && typeof interp.text === "string") {
        return interp.text;
      }
      if (interp?.coding && Array.isArray(interp.coding) && interp.coding[0]) {
        const coding = interp.coding[0] as Record<string, unknown>;
        if (coding.display && typeof coding.display === "string") {
          return coding.display;
        }
      }
    }
    return undefined;
  }

  function getInterpretationColor(interpretation: string): string {
    const lower = interpretation.toLowerCase();
    if (lower.includes("high") || lower.includes("critical")) return "red";
    if (lower.includes("low")) return "orange";
    if (lower.includes("normal")) return "green";
    return "blue";
  }

  function getReasonCodes(): Array<{ display: string; code: string }> {
    const data = suspectData as Record<string, unknown>;
    if (!data.reasonCode || !Array.isArray(data.reasonCode)) {
      return [];
    }

    return data.reasonCode.flatMap((reasonCodeItem: unknown) => {
      const item = reasonCodeItem as Record<string, unknown>;
      if (!item.coding || !Array.isArray(item.coding)) {
        return [];
      }
      return item.coding
        .map((coding: unknown) => {
          const codingObj = coding as Record<string, unknown>;
          return {
            display:
              codingObj.display && typeof codingObj.display === "string" ? codingObj.display : "",
            code: codingObj.code && typeof codingObj.code === "string" ? codingObj.code : "",
          };
        })
        .filter(item => item.display || item.code);
    });
  }

  function getComponents(): Array<{ name: string; value: string }> {
    const data = suspectData as Record<string, unknown>;
    if (!data.component || !Array.isArray(data.component)) {
      return [];
    }

    return data.component
      .map((comp: unknown) => {
        const component = comp as Record<string, unknown>;
        const code = component.code as Record<string, unknown> | undefined;
        let name = "Component";
        if (code?.coding && Array.isArray(code.coding) && code.coding[0]) {
          const coding = code.coding[0] as Record<string, unknown>;
          if (coding.display && typeof coding.display === "string") {
            name = coding.display;
          }
        } else if (code?.text && typeof code.text === "string") {
          name = code.text;
        }

        let value = "";
        if (component.valueQuantity) {
          const vq = component.valueQuantity as Record<string, unknown>;
          const val = vq.value;
          const unit = vq.unit && typeof vq.unit === "string" ? vq.unit : "";
          value = `${val} ${unit}`.trim();
        } else if (component.valueString && typeof component.valueString === "string") {
          value = component.valueString;
        }

        return value ? { name, value } : undefined;
      })
      .filter((c: unknown): c is { name: string; value: string } => c !== undefined);
  }

  const name = getName();
  const code = getCode();
  const value = getValue();
  const effectiveDate = getEffectiveDate();
  const interpretation = getInterpretation();
  const reasonCodes = getReasonCodes();
  const components = getComponents();
  const status =
    suspectData.status && typeof suspectData.status === "string" ? suspectData.status : undefined;

  return (
    <Box border="1px" borderColor="border" borderRadius="lg" p={4} bg="bg" shadow="sm">
      <VStack gap={4} align="stretch">
        <Flex justify="space-between" align="start" gap={3}>
          <HStack gap={4} flexWrap="wrap">
            <Badge
              colorPalette={getResourceTypeColor(resourceType)}
              variant="subtle"
              fontSize="xs"
              px={2}
              py={1}
              flexShrink={0}
            >
              {resourceType}
            </Badge>
            {effectiveDate && (
              <Text fontSize="sm" fontWeight="medium" color="fg">
                {effectiveDate}
              </Text>
            )}
          </HStack>
          {status && (
            <Badge colorPalette="blue" fontSize="xs" px={2} py={1}>
              {status}
            </Badge>
          )}
        </Flex>

        <Flex justify="space-between" align="start" gap={3}>
          <Heading size="md" color="fg" lineClamp={1} flex={1}>
            {name}
          </Heading>
          {dataSource && (
            <ButtonGroup size="sm" variant="outline" backgroundColor="bg.subtle" attached>
              <Button
                variant="outline"
                _hover={{ backgroundColor: "bg.muted" }}
                onClick={() => onViewPdf?.(dataSource)}
              >
                View PDF
              </Button>
              <Menu.Root positioning={{ placement: "bottom-end" }}>
                <Menu.Trigger asChild>
                  <IconButton
                    variant="outline"
                    borderLeftRadius={0}
                    _hover={{ backgroundColor: "bg.muted" }}
                  >
                    <FaChevronDown />
                  </IconButton>
                </Menu.Trigger>
                <Menu.Positioner>
                  <Menu.Content>
                    <Menu.Item
                      value="html"
                      onClick={() =>
                        onDownloadFile({
                          docId: dataSource,
                          docFilename: dataSource,
                          conversionType: "html",
                        })
                      }
                      cursor="pointer"
                      disabled={
                        isDownloading[dataSource]?.downloading &&
                        isDownloading[dataSource]?.type === "html"
                      }
                    >
                      {isDownloading[dataSource]?.downloading &&
                      isDownloading[dataSource]?.type === "html"
                        ? "Downloading HTML..."
                        : "Download HTML"}
                    </Menu.Item>
                    <Menu.Item
                      value="xml"
                      onClick={() =>
                        onDownloadFile({
                          docId: dataSource,
                          docFilename: dataSource,
                        })
                      }
                      cursor="pointer"
                      disabled={
                        isDownloading[dataSource]?.downloading &&
                        isDownloading[dataSource]?.type === "xml"
                      }
                    >
                      {isDownloading[dataSource]?.downloading &&
                      isDownloading[dataSource]?.type === "xml"
                        ? "Downloading XML..."
                        : "Download XML"}
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
            </ButtonGroup>
          )}
        </Flex>

        {code && (
          <HStack gap={2}>
            <Text fontSize="xs" color="fg.muted" fontWeight="medium">
              Code:
            </Text>
            <Text fontSize="xs" color="fg.muted" fontFamily="mono">
              {code}
            </Text>
          </HStack>
        )}

        {reasonCodes.length > 0 && (
          <>
            <Separator borderColor="border" />
            <Box bg="bg.subtle" p={3} borderRadius="md" borderLeft="3px" borderColor="purple.500">
              <Text fontSize="sm" color="fg" fontWeight="semibold" mb={2}>
                Reason Code
              </Text>
              <VStack gap={2} align="stretch">
                {reasonCodes.map((reasonCode, index) => (
                  <HStack key={index} gap={2}>
                    <Badge colorPalette="purple" variant="subtle" fontSize="xs" fontFamily="mono">
                      {reasonCode.code}
                    </Badge>
                    <Text fontSize="sm" color="fg">
                      {reasonCode.display}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </>
        )}

        {value && (
          <Box bg="blue.50" borderLeft="4px" borderColor="blue.500" p={3} borderRadius="md">
            <HStack justify="space-between" align="center">
              <VStack align="start" gap={0}>
                <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                  Value
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="fg">
                  {value}
                </Text>
              </VStack>
              {interpretation && (
                <Badge
                  colorPalette={getInterpretationColor(interpretation)}
                  fontSize="sm"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {interpretation}
                </Badge>
              )}
            </HStack>
          </Box>
        )}

        {components.length > 0 && (
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="fg" mb={2}>
              Components
            </Text>
            <VStack gap={2} align="stretch">
              {components.map((component, index) => (
                <HStack key={index} bg="bg.subtle" p={2} borderRadius="md" justify="space-between">
                  <Text fontSize="sm" color="fg.muted">
                    {component.name}
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold" color="fg">
                    {component.value}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
}
